// CareLogix™ ORACLE — Simple Mode / Advanced Mode (Phase 42C).
//
// Default = Simple. Persisted under `oracle_ux_mode`. Broadcast via a
// custom event so any component can react without prop drilling.
import { useCallback, useEffect, useState } from "react";

const KEY = "oracle_ux_mode";
const SIMPLE   = "simple";
const ADVANCED = "advanced";
const EVT = "oracle:ux-mode-changed";

function readPersisted() {
  try {
    const v = localStorage.getItem(KEY);
    return v === ADVANCED ? ADVANCED : SIMPLE;
  } catch { return SIMPLE; }
}

export function useOracleMode() {
  const [mode, setMode] = useState(() => readPersisted());

  useEffect(() => {
    function onChange(e) {
      const next = e?.detail?.mode === ADVANCED ? ADVANCED : SIMPLE;
      setMode(next);
    }
    function onStorage(e) { if (e.key === KEY) setMode(readPersisted()); }
    try {
      window.addEventListener(EVT, onChange);
      window.addEventListener("storage", onStorage);
    } catch { /* ignore */ }
    return () => {
      try {
        window.removeEventListener(EVT, onChange);
        window.removeEventListener("storage", onStorage);
      } catch { /* ignore */ }
    };
  }, []);

  const setOracleMode = useCallback((next) => {
    const v = next === ADVANCED ? ADVANCED : SIMPLE;
    try {
      localStorage.setItem(KEY, v);
      window.dispatchEvent(new CustomEvent(EVT, { detail: { mode: v } }));
    } catch { /* ignore */ }
    setMode(v);
  }, []);

  const toggleOracleMode = useCallback(() => {
    setOracleMode(mode === SIMPLE ? ADVANCED : SIMPLE);
  }, [mode, setOracleMode]);

  return {
    mode,
    isSimple: mode === SIMPLE,
    isAdvanced: mode === ADVANCED,
    setOracleMode,
    toggleOracleMode,
    SIMPLE, ADVANCED,
  };
}

export default useOracleMode;
